/**
 * Click-to-move input adapter — GH#8 Phase 1 J1 completion.
 *
 * Pure routing helpers that translate a canvas-click + modifier state +
 * the local player's position into one of:
 *
 *   - `move`    — orthogonally-adjacent left-click; dispatch a `MoveIntent`
 *                 with the derived `CardinalDirection`.
 *   - `moveTo`  — non-adjacent left-click; dispatch a `MoveToIntent` for
 *                 server-side A* pathfinding. No client-side walkability
 *                 check per I6 — server rejects with `NoPath` if unreachable.
 *   - `inspect` — modifier-click, click on self-tile, or no self-player yet.
 *                 Shift-click inspects adjacent tiles without moving.
 *
 * Server-authoritative per I6 — the move path dispatches the intent and
 * waits for the resulting `PlayerMoved` event to update the avatar's
 * rendered position. No optimistic update; the renderer still owns the
 * 1-tick interpolation animation it had before this directive.
 *
 * Coord convention per WORLD_MODEL §2: integer `(x, y)` tile space with
 * `+x = East`, `+y = South`. Cardinal mapping below uses that.
 */

import type { CardinalDirection, TileCoord } from '../wire';

/** Discriminated outcome of `routeTileClick`. */
export type ClickAction =
  | { kind: 'move'; direction: CardinalDirection }
  | { kind: 'moveTo'; target: TileCoord }
  | { kind: 'inspect' };

/**
 * Pure: derive `CardinalDirection` from a `selfTile → clickTile` delta.
 * Returns `null` for diagonals, non-adjacent clicks, or clicks on the
 * self-tile itself. Manhattan distance must be exactly 1.
 */
export function cardinalFromDelta(
  selfTile: TileCoord,
  clickTile: TileCoord,
): CardinalDirection | null {
  const dx = clickTile[0] - selfTile[0];
  const dy = clickTile[1] - selfTile[1];
  if (Math.abs(dx) + Math.abs(dy) !== 1) return null;
  if (dx === 1) return 'East';
  if (dx === -1) return 'West';
  if (dy === 1) return 'South';
  return 'North';
}

/**
 * Pure click router. The caller wires the chosen `ClickAction` to its
 * concrete handlers (live-wire send for `move`/`moveTo`, inspect-store
 * opener for `inspect`).
 *
 * Disambiguation (priority order):
 *   1. `shiftKey` modifier → inspect (lets operator inspect any tile
 *      without moving onto it)
 *   2. `selfTile === null` → inspect (no local player → can't move)
 *   3. Click on self-tile → inspect
 *   4. Cardinally-adjacent → move (single-step cardinal MoveIntent)
 *   5. Non-adjacent → moveTo (server-side A* pathfinding via MoveToIntent)
 */
export function routeTileClick(args: {
  selfTile: TileCoord | null;
  clickTile: TileCoord;
  shiftKey: boolean;
}): ClickAction {
  if (args.shiftKey || args.selfTile === null) return { kind: 'inspect' };
  if (
    args.clickTile[0] === args.selfTile[0] &&
    args.clickTile[1] === args.selfTile[1]
  ) {
    return { kind: 'inspect' };
  }
  const direction = cardinalFromDelta(args.selfTile, args.clickTile);
  if (direction !== null) return { kind: 'move', direction };
  return { kind: 'moveTo', target: args.clickTile };
}
